import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import apiClient from "../apiClient";
import { adminKeys } from "./queryKeys";
import {
  AdminOrganization,
  AdminUser,
  AutoEmailSendingMode,
  PageScope,
  UserRole,
} from "../../utils/types";
import { toast } from "sonner";

export const useAdminOrganizationsQuery = (
  options?: Omit<
    UseQueryOptions<
      AdminOrganization[],
      unknown,
      AdminOrganization[],
      string[]
    >,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [...adminKeys.organizations],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminOrganization[]>(
        "/admin/organizations",
      );
      return data;
    },
    ...options,
  });

type CreateOrganizationInput = {
  name: string;
  support_email: string;
  official_outgoing_email?: string | null;
  use_official_outgoing_email?: boolean;
  accounting_software?: string | null;
  auto_email_sending_mode?: AutoEmailSendingMode | null;
  priority_api_url?: string | null;
};

export const useCreateOrganizationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateOrganizationInput) => {
      const { data } = await apiClient.post<AdminOrganization>(
        "/admin/organizations",
        payload,
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: adminKeys.organizations,
      });
      await queryClient.invalidateQueries({
        queryKey: ["organization", "outgoing-mailbox"],
      });
      if (data.outgoing_mailbox_setup_warning) {
        toast.warning("Outgoing mailbox setup", {
          description: data.outgoing_mailbox_setup_warning,
        });
      }
    },
  });
};

type UpdateOrganizationInput = CreateOrganizationInput & { id: number };

export const useUpdateOrganizationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateOrganizationInput) => {
      const { data } = await apiClient.patch<AdminOrganization>(
        `/admin/organizations/${id}`,
        payload,
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: adminKeys.organizations,
      });
      await queryClient.invalidateQueries({
        queryKey: ["organization", "outgoing-mailbox"],
      });
      if (data.outgoing_mailbox_setup_warning) {
        toast.warning("Outgoing mailbox setup", {
          description: data.outgoing_mailbox_setup_warning,
        });
      }
    },
  });
};

export const useAdminUsersQuery = () =>
  useQuery({
    queryKey: [...adminKeys.users],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminUser[]>("/admin/users");
      return data;
    },
  });

type UpdateUserInput = {
  userId: number;
  roles?: UserRole;
  budy_organization_ids?: number[];
  page_scopes?: PageScope[];
};

type ZohoAuthorizeResponse = {
  authorization_url: string;
  state: string;
};

type InitZohoTokenInput = {
  refresh_token?: string;
  authorization_code?: string;
  redirect_uri?: string;
  accounts_base?: string;
  crm_base?: string;
};

type InitZohoTokenResponse = {
  status: string;
  message: string;
  endpoint_access_token: string;
};

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, ...payload }: UpdateUserInput) => {
      const { data } = await apiClient.patch<AdminUser>(
        `/admin/users/${userId}`,
        payload,
      );
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminKeys.users }),
        queryClient.invalidateQueries({ queryKey: adminKeys.organizations }),
      ]);
    },
  });
};

export const useZohoAuthorizeUrlMutation = () =>
  useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<ZohoAuthorizeResponse>(
        "/zoho/oauth/authorize",
      );
      return data;
    },
  });

export const useInitZohoTokenMutation = () =>
  useMutation({
    mutationFn: async (payload: InitZohoTokenInput) => {
      const { data } = await apiClient.post<InitZohoTokenResponse>(
        "/zoho/internal/token",
        payload,
      );
      return data;
    },
  });

export type OrgMicrosoftOAuthConfig = {
  tenant_id: string;
  client_id: string;
  redirect_uri: string;
  has_client_secret: boolean;
  configured: boolean;
  source: string;
};

type UpdateOrgMicrosoftOAuthInput = {
  orgId: number;
  tenant_id: string;
  client_id: string;
  redirect_uri: string;
  client_secret?: string;
};

export const useOrgMicrosoftOAuthQuery = (orgId: number | null) =>
  useQuery({
    queryKey: [...adminKeys.organizations, "microsoft-oauth", orgId],
    queryFn: async () => {
      const { data } = await apiClient.get<OrgMicrosoftOAuthConfig>(
        `/admin/organizations/${orgId}/microsoft-oauth`,
      );
      return data;
    },
    enabled: orgId !== null,
  });

export const useUpdateOrgMicrosoftOAuthMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, ...payload }: UpdateOrgMicrosoftOAuthInput) => {
      const { data } = await apiClient.put<OrgMicrosoftOAuthConfig>(
        `/admin/organizations/${orgId}/microsoft-oauth`,
        payload,
      );
      return data;
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          ...adminKeys.organizations,
          "microsoft-oauth",
          variables.orgId,
        ],
      });
    },
  });
};

export const useDeleteOrgMicrosoftOAuthMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orgId: number) => {
      const { data } = await apiClient.delete<{ deleted: boolean }>(
        `/admin/organizations/${orgId}/microsoft-oauth`,
      );
      return data;
    },
    onSuccess: async (_data, orgId) => {
      await queryClient.invalidateQueries({
        queryKey: [...adminKeys.organizations, "microsoft-oauth", orgId],
      });
    },
  });
};

export type DraftValidationListItem = {
  draft_uid: string;
  customer_name: string;
  payment_external_ids: string[];
  subject: string;
  created_at: string;
  feedback_sentiment?: string | null;
  has_trace?: boolean;
  trace_status?: string | null;
  prompt_template_key?: string | null;
  model_name?: string | null;
  composer_edit_distance?: number | null;
};

export type ComposerEditSummary = {
  edit_distance: number;
  generated_html: string;
  edited_html: string;
};

export type DraftFeedbackSummary = {
  sentiment: string;
  reason_category?: string | null;
  feedback_text?: string | null;
  created_at?: string;
};

export type LatestMessage = {
  from_email: string;
  from_name: string | null;
  to: string;
  subject: string;
  date_sent: string;
  text: string;
};

export type DraftGenerationAttemptSummary = {
  id: string;
  attempt_index: number;
  attempt_kind: string;
  prompt_role: string;
  model_provider?: string | null;
  model_name?: string | null;
  escalated?: boolean | null;
  latency_seconds?: number | null;
  prompt_template_key?: string | null;
  prompt_content_hash?: string | null;
  prompt_system?: string | null;
  prompt_user?: string | null;
  raw_llm_response?: string | null;
  guardrail_verdict?: string | null;
  guardrail_detail?: string | null;
  created_at: string;
};

export type DraftGenerationSummary = {
  trace_id: string;
  trace_kind: string;
  status: string;
  language?: string | null;
  created_at: string;
  input_snapshot?: Record<string, unknown> | null;
  attempts: DraftGenerationAttemptSummary[];
};

export type DraftValidationDetail = {
  draft_text: string;
  thread_state_facts: string;
  latest_message: LatestMessage | null;
  customer_name: string;
  payment_external_ids: string[];
  subject: string;
  to: string[];
  cc?: string[] | null;
  bcc?: string[] | null;
  thread_id?: string | null;
  reply_message_id?: string | null;
  trace_id?: string | null;
  generation?: DraftGenerationSummary | null;
  user_feedback?: DraftFeedbackSummary | null;
  composer_edit?: ComposerEditSummary | null;
};

type AdminDraftUpdatePayload = {
  text: string;
  to: string[];
  subject: string;
  cc?: string[] | null;
  bcc?: string[] | null;
  thread_id?: string | null;
  reply_message_id?: string | null;
};

export type DraftValidationOrgSummary = {
  total: number;
  traced: number;
  edited: number;
  with_feedback: number;
};

type PaginatedDraftValidationResponse = {
  items: DraftValidationListItem[];
  total: number;
  skip: number;
  limit: number;
};

export const DRAFT_VALIDATION_PAGE_SIZE = 50;

export const useDraftValidationSummaryQuery = (
  orgId: number | null,
  options?: Omit<
    UseQueryOptions<
      DraftValidationOrgSummary,
      unknown,
      DraftValidationOrgSummary,
      (string | number | null)[]
    >,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [...adminKeys.draftValidation, "summary", orgId],
    queryFn: async () => {
      if (!orgId) {
        return { total: 0, traced: 0, edited: 0, with_feedback: 0 };
      }
      const { data } = await apiClient.get<DraftValidationOrgSummary>(
        "/admin/draft-validation/summary",
        { params: { org_id: orgId } },
      );
      return data;
    },
    enabled: orgId !== null,
    ...options,
  });

export const useDraftValidationListQuery = (
  orgId: number | null,
  page: number,
  pageSize: number = DRAFT_VALIDATION_PAGE_SIZE,
  options?: Omit<
    UseQueryOptions<
      PaginatedDraftValidationResponse,
      unknown,
      PaginatedDraftValidationResponse,
      (string | number | null)[]
    >,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [...adminKeys.draftValidation, "list", orgId, page, pageSize],
    queryFn: async () => {
      if (!orgId) {
        return { items: [], total: 0, skip: 0, limit: pageSize };
      }
      const skip = (page - 1) * pageSize;
      const { data } = await apiClient.get<PaginatedDraftValidationResponse>(
        "/admin/draft-validation/list",
        { params: { org_id: orgId, skip, limit: pageSize } },
      );
      return data;
    },
    enabled: orgId !== null,
    ...options,
  });

export const useDraftValidationDetailQuery = (
  draftUid: string | null,
  orgId: number | null,
  options?: Omit<
    UseQueryOptions<
      DraftValidationDetail,
      unknown,
      DraftValidationDetail,
      (string | number | null)[]
    >,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [...adminKeys.draftValidation, "detail", draftUid, orgId],
    queryFn: async () => {
      if (!draftUid || !orgId) {
        throw new Error("draftUid and orgId are required");
      }
      const res = await apiClient.get<DraftValidationDetail>(
        "/admin/draft-validation/detail",
        {
          params: {
            draft_uid: draftUid,
            org_id: orgId,
          },
        },
      );
      const raw = res.data;
      if (typeof raw === "string") {
        return JSON.parse(raw) as DraftValidationDetail;
      }
      return raw;
    },
    enabled: draftUid !== null && orgId !== null,
    ...options,
  });

export const useUpdateAdminDraftMutation = (
  draftUid: string | null,
  orgId: number | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AdminDraftUpdatePayload) => {
      await apiClient.patch("/admin/draft-validation/draft", payload, {
        params: { draft_uid: draftUid, org_id: orgId },
      });
    },
    onSuccess: () => {
      if (draftUid && orgId) {
        queryClient.invalidateQueries({
          queryKey: [...adminKeys.draftValidation, "detail", draftUid, orgId],
        });
        queryClient.invalidateQueries({
          queryKey: [...adminKeys.draftValidation, "list", orgId],
        });
        queryClient.invalidateQueries({
          queryKey: [...adminKeys.draftValidation, "summary", orgId],
        });
      }
    },
  });
};
